import { Link } from 'react-router-dom';
import './DetailHeroCard.css';

function renderAction(action) {
  const classes = [
    'detail-hero-card__action',
    action?.variant === 'secondary'
      ? 'detail-hero-card__action--secondary'
      : 'detail-hero-card__action--primary',
  ].join(' ');

  const content = (
    <>
      {action?.icon ? <i className={action.icon} aria-hidden="true" /> : null}
      <span>{action?.label}</span>
    </>
  );

  if (action?.to) {
    return (
      <Link key={action.key || action.label} className={classes} to={action.to}>
        {content}
      </Link>
    );
  }

  return (
    <button
      key={action?.key || action?.label}
      type="button"
      className={classes}
      onClick={action?.onClick}
    >
      {content}
    </button>
  );
}

export default function DetailHeroCard({
  backTo,
  backLabel,
  backPlacement = 'topbar',
  title,
  titleAside = null,
  badge,
  subtitle,
  metaItems = [],
  contextInline = false,
  actions = [],
  visual = null,
  statsRows = [],
  contentFooter = null,
  className = '',
}) {
  const backLink = backTo ? (
    <Link className="detail-hero-card__back" to={backTo}>
      <i className="pi pi-arrow-left" aria-hidden="true" />
      <span>{backLabel}</span>
    </Link>
  ) : null;
  const showTopbar = (backLink && backPlacement !== 'title') || actions.length;

  return (
    <section className={['detail-hero-card', className].filter(Boolean).join(' ')}>
      <div className="detail-hero-card__visual">
        {visual}
      </div>

      <div className="detail-hero-card__content">
        {showTopbar ? (
          <div className="detail-hero-card__topbar" data-testid="detail-hero-topbar">
            {backPlacement !== 'title' ? backLink || <span /> : <span />}

            {actions.length ? (
              <div className="detail-hero-card__actions">
                {actions.map((action) => renderAction(action))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="detail-hero-card__title-row" data-testid="detail-hero-title-row">
          <h1 className="detail-hero-card__title">{title}</h1>
          {backPlacement === 'title' ? backLink : null}
          {titleAside ? (
            <div className="detail-hero-card__title-aside">
              {titleAside}
            </div>
          ) : badge?.label ? (
            <span
              className={[
                'detail-hero-card__badge',
                `detail-hero-card__badge--${badge.tone || 'default'}`,
              ].join(' ')}
              title={badge.title || badge.label}
            >
              {badge.label}
            </span>
          ) : null}
        </div>

        {(subtitle || metaItems.length) ? (
          <div className={contextInline ? 'detail-hero-card__context detail-hero-card__context--inline' : 'detail-hero-card__context'}>
            {subtitle ? <p className="detail-hero-card__subtitle">{subtitle}</p> : null}
            {metaItems.length ? (
              <div className="detail-hero-card__meta">
                {metaItems.map((item) => {
                  const content = <>{item.icon ? <i className={item.icon} aria-hidden="true" /> : null}{item.label}</>;
                  const itemKey = item.key || `${item.icon || 'meta'}-${typeof item.label === 'string' ? item.label : 'content'}`;
                  if (item.to) return <Link key={itemKey} className="detail-hero-card__meta-link" to={item.to} title={item.title || undefined}>{content}</Link>;
                  if (item.href) return <a key={itemKey} className="detail-hero-card__meta-link" href={item.href} target={item.target} rel={item.rel} title={item.title || undefined}>{content}</a>;
                  return <span key={itemKey} title={item.title || undefined}>{content}</span>;
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        {statsRows.length ? (
          <div className="detail-hero-card__stats">
            {statsRows.map((row, rowIndex) => (
              <div key={`hero-row-${rowIndex}`} className="detail-hero-card__stats-row">
                {row.map((stat) => (
                  <div key={stat.label} className="detail-hero-card__stat">
                    <span className="detail-hero-card__stat-label">{stat.label}</span>
                    <strong
                      className={[
                        'detail-hero-card__stat-value',
                        stat.tone ? `detail-hero-card__stat-value--${stat.tone}` : '',
                      ].filter(Boolean).join(' ')}
                      title={stat.title || (typeof stat.value === 'string' ? stat.value : undefined)}
                    >
                      {stat.value}
                    </strong>
                    <span className="detail-hero-card__stat-sub">{stat.sub || '—'}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}

        {contentFooter ? (
          <div className="detail-hero-card__content-footer">
            {contentFooter}
          </div>
        ) : null}
      </div>
    </section>
  );
}
